import {Box} from '@mui/material'
import moment from 'moment'
import React, {useEffect, useMemo, useState} from 'react'

import {useTimePickerStyles} from './styles'
import {TimePickerProps, SelectedIndexesState, WheelPickerColumn, ClockSystem} from './types'
import {
  getAvailableUnits,
  getClockSystem,
  getHourForIndex,
  getMaxDateTime,
  getMinDateTime,
  getMinutesForIndex,
  getTimeUnitForIndexOrUndefined,
  getValidHours,
  isEndOfDay,
  isStartOfDay,
  normalizeTime,
  updateDate
} from './utils'
import {WheelPicker} from './WheelPicker'

export const TimePicker: React.FC<TimePickerProps> = ({
  initialTime,
  minTime,
  maxTime,
  onSelectedTimeChange,
  testID = 'rmc-time-picker'
}) => {
  const has12HoursFormat = getClockSystem() === ClockSystem['12H']

  const classes = useTimePickerStyles()

  const [selectedIndexes, setSelectedIndexes] = useState<SelectedIndexesState>({
    hour: undefined,
    minutes: undefined,
    unit: undefined
  })

  const shouldCheckMinAndMaxTime = useMemo(() => {
    return minTime && maxTime && (!isStartOfDay(minTime) || !isEndOfDay(maxTime))
  }, [minTime, maxTime])

  const allowedHours = useMemo(() => {
    return getValidHours(minTime, maxTime)
  }, [minTime, maxTime])

  // Create base columns structure without time constraints
  const baseColumns = useMemo(() => {
    if (has12HoursFormat) {
      const units = getAvailableUnits(minTime, maxTime)
      return [
        {values: allowedHours, separator: ':'},
        {values: [...Array(4).keys()].map((i) => (i === 0 ? '00' : String(i * 15)))},
        {values: units}
      ]
    }
    return [
      {values: allowedHours, separator: ':'},
      {values: [...Array(4).keys()].map((i) => (i === 0 ? '00' : String(i * 15)))} // should time change interval "15" be hardcoded. should we get it from configs ?
    ]
  }, [allowedHours, minTime, maxTime, has12HoursFormat])

  // Apply time constraints to columns without triggering state changes
  const columns: WheelPickerColumn[] = useMemo(() => {
    const {hour, minutes, unit} = selectedIndexes

    // Deep clone base columns to avoid mutations
    const columnsInner = baseColumns.map((col) => ({
      ...col,
      values: [...col.values]
    }))

    // Apply time constraints if we have a valid selection
    if (hour !== undefined && minutes !== undefined && shouldCheckMinAndMaxTime) {
      const hourForIndex = getHourForIndex(columnsInner, hour)
      const minutesForIndex = getMinutesForIndex(columnsInner, minutes)
      const timeUnitForIndex = getTimeUnitForIndexOrUndefined(columnsInner, unit)
      const selectedTime = updateDate(initialTime, hourForIndex, minutesForIndex, timeUnitForIndex)

      const minDateTime = getMinDateTime(selectedTime, minTime)
      const maxDateTime = getMaxDateTime(selectedTime, maxTime)
      const isMinHour = selectedTime.hours() === minDateTime.hours()
      const isMaxHour = selectedTime.hours() === maxDateTime.hours()

      if (isMaxHour) {
        const maxDateTimeMinutes = getMaxDateTime(selectedTime, maxTime).format('mm')
        columnsInner[1] = {
          ...columnsInner[1],
          values: columnsInner[1].values.filter((i) => Number(i) <= Number(maxDateTimeMinutes))
        }
      }

      if (isMinHour) {
        const minDateTimeMinutes = getMinDateTime(selectedTime, minTime).format('mm')
        columnsInner[1] = {
          ...columnsInner[1],
          values: columnsInner[1].values.filter((i) => Number(i) >= Number(minDateTimeMinutes))
        }
      }
    }

    return columnsInner
  }, [selectedIndexes, baseColumns, initialTime, minTime, maxTime, shouldCheckMinAndMaxTime])

  // Initialize selected indexes from initial time (only on mount or when initialTime changes)
  useEffect(() => {
    const minDateTime = getMinDateTime(initialTime, minTime)
    const maxDateTime = getMaxDateTime(initialTime, maxTime)
    const normalizedTime = normalizeTime(initialTime, minDateTime, maxDateTime)

    const minute = normalizedTime.minutes()
    const minuteIndex = Math.floor(minute / 15)
    const initialHour = normalizedTime.format(has12HoursFormat ? 'h' : 'H')

    if (has12HoursFormat) {
      const currentTime = moment(normalizedTime).format('LT').toLocaleUpperCase()
      const timeUnit = currentTime.split(' ')[1]
      const units = getAvailableUnits(minTime, maxTime)
      const timeUnitIndex = units.indexOf(timeUnit)
      const hourIndex = allowedHours.indexOf(initialHour) % 12

      setSelectedIndexes({
        hour: hourIndex >= 0 ? hourIndex : 0,
        minutes: minuteIndex,
        unit: timeUnitIndex >= 0 ? timeUnitIndex : 0
      })
    } else {
      const hourIndex = allowedHours.indexOf(initialHour)
      setSelectedIndexes({
        hour: hourIndex >= 0 ? hourIndex : 0,
        minutes: minuteIndex,
        unit: undefined
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTime, minTime, maxTime, has12HoursFormat])

  // Handle column selection changes
  const onSelectedIndex = (columnIndex: number, index: number) => {
    const indexes = ['hour', 'minutes', 'unit'] as const
    const selectedColumn = indexes[columnIndex]

    if (selectedColumn && index !== selectedIndexes[selectedColumn]) {
      const newIndexes = {...selectedIndexes, [selectedColumn]: index}
      setSelectedIndexes(newIndexes)

      // Call onSelectedTimeChange immediately when user makes a selection
      const {hour, minutes, unit} = newIndexes
      if (hour !== undefined && minutes !== undefined) {
        const hourForIndex = getHourForIndex(columns, hour)
        const minutesForIndex = getMinutesForIndex(columns, minutes)
        const timeUnitForIndex = getTimeUnitForIndexOrUndefined(columns, unit)
        const selectedTime = updateDate(
          initialTime,
          hourForIndex,
          minutesForIndex,
          timeUnitForIndex
        )

        onSelectedTimeChange(selectedTime)
      }
    }
  }
  // Calculate picker selected indexes for the WheelPicker
  const pickerSelectedIndexes = useMemo((): number[] => {
    if (
      selectedIndexes.hour === undefined ||
      selectedIndexes.minutes === undefined ||
      (has12HoursFormat && selectedIndexes.unit === undefined)
    ) {
      return []
    }
    if (has12HoursFormat) {
      return [selectedIndexes.hour, selectedIndexes.minutes, selectedIndexes.unit ?? 0]
    }
    return [selectedIndexes.hour, selectedIndexes.minutes]
  }, [selectedIndexes.hour, selectedIndexes.minutes, selectedIndexes.unit])

  if (pickerSelectedIndexes.length === 0) {
    return null
  }

  return (
    <Box className={classes.container} data-testid={testID}>
      <WheelPicker
        testID={`${testID}-wheel`}
        selectedIndexes={pickerSelectedIndexes}
        columns={columns}
        onSelectedIndex={onSelectedIndex}
        visibleRows={5}
      />
    </Box>
  )
}
